/* eslint-disable */
// Vendored from the TM-8427 Jira attachment (final-regex-parser.zip, 26 Oct 2026) – a rule-based parser
// maintained outside this repo and re-tuned on logged Client text. Replace the whole file on an update instead of
// editing it, and ship the matching vocab under a new name in public/own-words-search (see VOCAB_URL in
// own-words-search.data.js).
//
// Local changes, to re-apply on every update unless the new version already has them:
// 1. Regex literals with a lookbehind ("(?<!") are written as new RegExp(String.raw`...`). Safari before 16.4
//    rejects lookbehind literals while parsing the script, which would take the whole Location Page bundle down for
//    every visitor; a constructor only throws when called.
// 2. Budget ranges: "£500 to £3k" no longer applies the "k" to the lower bound (it gave min £500,000, max £3,000).
// 3. Named-month dates keep a year typed after them ("20 December 2027"), like numeric dates already did.

/**
 * Free-text LP search parser – rule-based (regex + vocabulary), no AI.
 *
 * parseSearch(text, ctx, vocab) -> filters object (same shape as the AI prompt v5 output,
 * minus the free-wording fields).
 *
 *   ctx   = { lpCity: "Manchester", country: "GB", today: "2026-10-19", lpEventSlug: null }
 *   vocab = contents of vocab.json (event/space phrases from DMS Room Tags, Location Tree areas)
 *
 * Runs in the browser or Node, ~1 ms per search, no network calls.
 */

// ---------------------------------------------------------------- helpers
const NUM_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60,
  seventy: 70, eighty: 80, ninety: 90, dozen: 12,
};
const ORDINAL_WORDS = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9,
  tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14, fifteenth: 15,
  sixteenth: 16, seventeenth: 17, eighteenth: 18, nineteenth: 19, twentieth: 20,
  "twenty-first": 21, "twenty first": 21, thirtieth: 30, fortieth: 40, fiftieth: 50,
  sixtieth: 60, seventieth: 70, eightieth: 80, ninetieth: 90, hundredth: 100,
};
const PEOPLE_WORDS =
  "people|persons?|guests?|ppl|pax|attendees|delegates|colleagues|staff|employees|members|friends|family|" +
  "kids|children|adults|teens|teenagers|lads|girls|ladies|guys|blokes|boys|women|men|crew|journalists|" +
  "founders|clients|coworkers|co-workers|musicians|students|participants|heads|covers|folks|of us|" +
  "invitees|visitors|speakers|reps|partners|couples|toddlers|mates|pals|buddies|peeps|souls|bodies";
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august",
  "september", "october", "november", "december"];
const MON_ABBR = { jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11 };
const MON_ANY = "(?:" + MONTHS.join("|") + "|sept|" + Object.keys(MON_ABBR).filter((k) => k !== "sept").join("|") + ")";
const monIndex = (w) => (MONTHS.indexOf(w) > -1 ? MONTHS.indexOf(w) : MON_ABBR[w]);
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const NOT_SEARCH = /\b(i (have )?booked|my booking|my existing booking|existing booking|my enquiry|my reservation|refund|cancel my|list my venue|list a venue|my venue on|invoice|receipt|password|log ?in|sign ?in|customer service|contact you|my account)\b/;
const NEGATORS = /\b(nothing (?:too |overly |very )?|not (?:in |at )?(?:a |an |the )?|no |without |rather than |anything but |except |avoid |don't want (?:a |an )?|dont want (?:a |an )?)/g;

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const wordRe = (phrase) => new RegExp("(?<![a-z0-9])" + esc(phrase) + "(?![a-z0-9])", "g");

function lev(a, b) {
  if (Math.abs(a.length - b.length) > 2) return 9;
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++) {
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  return d[a.length][b.length];
}

// Words we never "typo-correct" (common English that sits 1 edit away from vocabulary)
const KEEP = new Set(("about around after before people guests their there where which while would could should " +
  "please thanks hello venue venues space spaces somewhere place looking need needs want wants friends family " +
  "night nights evening morning afternoon weekend weekday budget under between maybe roughly approx little " +
  "small large big great nice lovely cheap pricey within close central centre center party dinner").split(" "));

// ---------------------------------------------------------------- vocabulary index (built once)
let INDEX = null;
function buildIndex(vocab) {
  const evPhrases = [];
  for (const [slug, e] of Object.entries(vocab.events))
    for (const p of e.phrases) evPhrases.push({ p, slug, tier: e.tier });
  const spPhrases = [];
  for (const [slug, ps] of Object.entries(vocab.spaces)) for (const p of ps) spPhrases.push({ p, slug });
  evPhrases.sort((a, b) => b.p.length - a.p.length);
  spPhrases.sort((a, b) => b.p.length - a.p.length);
  const words = new Set();
  for (const x of [...evPhrases, ...spPhrases]) for (const w of x.p.split(/\s+/)) if (w.length >= 5) words.add(w);
  for (const w of "projector whiteboard flipchart microphone speakers soundproof wheelchair elevator accessible catering caterers vegetarian kitchen halal kosher gluten refreshments cabaret classroom boardroom theatre theater standing seated cancellation supervenue ticketed wedding".split(" ")) words.add(w);
  const cityNames = new Map(); // lowercase city -> display
  for (const v of Object.values(vocab.locations)) cityNames.set(v.city.toLowerCase(), v.city);
  for (const v of Object.values(vocab.locations)) for (const a of v.areas) for (const w of a.toLowerCase().split(/\s+/)) if (w.length >= 5) words.add(w);
  for (const x of [...evPhrases, ...spPhrases]) x.re = wordRe(x.p);
  const byLen = new Map();
  for (const w of words) { if (!byLen.has(w.length)) byLen.set(w.length, []); byLen.get(w.length).push(w); }
  return { evPhrases, spPhrases, words, byLen, cityNames, common: new Set(vocab.common_words || []), typoCache: new Map() };
}

// ---------------------------------------------------------------- 1. normalise
function normalise(raw, idx) {
  let t = " " + raw.toLowerCase().replace(/[’'`]/g, "").replace(/[–—]/g, "-").replace(/\s+/g, " ") + " ";
  t = t.replace(/café/g, "cafe").replace(/\b(tmrw|tmr|tmrrw|tomoz|2moro|2morrow)\b/g, "tomorrow").replace(/\btheaters?\b/g, (w) => w.replace("theater", "theatre")); // US spelling
  // ordinal words -> "21st" style
  for (const [w, n] of Object.entries(ORDINAL_WORDS).sort((a, b) => b[0].length - a[0].length))
    t = t.replace(wordRe(w), n + "th");
  // "a hundred", "two hundred and fifty", "twenty-five"
  t = t.replace(/\b(a|one|two|three|four|five|six|seven|eight|nine)\s+hundred(?:\s+and)?(?:\s+(\w+))?/g, (m, a, b) => {
    const h = a === "a" ? 1 : NUM_WORDS[a];
    const rest = b && NUM_WORDS[b] !== undefined ? NUM_WORDS[b] : 0;
    return " " + (h * 100 + rest) + (b && NUM_WORDS[b] === undefined ? " " + b : "");
  });
  t = t.replace(/\b(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[- ](one|two|three|four|five|six|seven|eight|nine)\b/g,
    (m, a, b) => String(NUM_WORDS[a] + NUM_WORDS[b]));
  t = t.replace(/\b(a\s+)?dozen\b/g, "12");
  t = t.replace(/\ba (couple|few) (of )?hundred\b/g, (m, w) => (w === "couple" ? " 200" : " 300"));
  t = t.replace(/\b(a|one) (grand|k)\b/g, "1 $2");
  t = t.replace(/\b(\d+) and a half (grand|k|thousand)\b/g, "$1.5 $2");
  t = t.replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)\b/g,
    (m) => String(NUM_WORDS[m]));
  t = t.replace(/\b(\d+) and a half (grand|k|thousand)\b/g, "$1.5 $2");
  // typo correction against vocabulary words
  t = t.replace(/[a-z]{5,}/g, (w) => {
    if (KEEP.has(w) || idx.common.has(w) || idx.words.has(w)) return w;
    if (idx.typoCache.has(w)) return idx.typoCache.get(w);
    let best = null, bestD = 9, tie = false;
    const limit0 = w.length >= 8 ? 2 : 1;
    const pool = [];
    for (let L = w.length - limit0; L <= w.length + limit0; L++) if (idx.byLen.has(L)) pool.push(...idx.byLen.get(L));
    for (const v of pool) {
      const d = lev(w, v);
      if (d < bestD) { best = v; bestD = d; tie = false; } else if (d === bestD) tie = true;
    }
    const res = best && bestD <= limit0 && !tie ? best : w;
    if (idx.typoCache.size < 5000) idx.typoCache.set(w, res);
    return res;
  });
  return t;
}

// ---------------------------------------------------------------- 2. negated spans
function negatedSpans(t) {
  const spans = [];
  let m;
  NEGATORS.lastIndex = 0;
  while ((m = NEGATORS.exec(t))) {
    if (/^not (that|sure|bothered|fussed)|^nothing (over|above|more|under|less|else|much)/.test(t.slice(m.index))) continue;
    const start = m.index + m[0].length;
    const rest = t.slice(start);
    const stop = rest.search(/[,.;!?]| - | and | but | or | for | with | just | only /);
    spans.push([start, start + (stop === -1 ? rest.length : stop)]);
  }
  return spans;
}
const inSpans = (i, spans) => spans.some(([a, b]) => i >= a && i < b);

// ---------------------------------------------------------------- 3. numbers: budget, per head, guests
function extractNumbers(t, out) {
  const used = []; // [start,end] of number tokens already claimed
  const claim = (m) => used.push([m.index, m.index + m[0].length]);
  const free = (i) => !used.some(([a, b]) => i >= a && i < b);
  const money = (n, k) => Math.round(parseFloat(n.replace(/,/g, "")) * (k ? 1000 : 1));
  const K = "(k|grand|thousand)";

  // per head
  for (const m of t.matchAll(new RegExp("[£$€]?\\s?(\\d+(?:\\.\\d+)?)\\s?(?:pp|p\\.p\\.|per person|per head|a head|each|/head|/person|per pax|/pax|a pax|per guest|/guest|per cover|a cover)\\b", "g"))) {
    out.price_per_person_max = Math.round(parseFloat(m[1])); claim(m);
  }
  // range budgets: "between £2k and £3k", "£2-3k"
  let m = t.match(new RegExp("(?:between\\s+)?[£$€](\\d[\\d,.]*)\\s?" + K + "?\\s?(?:-|to|and)\\s?[£$€]?(\\d[\\d,.]*)\\s?" + K + "?"));
  if (!m) m = t.match(new RegExp("between\\s+(\\d[\\d,.]*)\\s?" + K + "?\\s?(?:and|-|to)\\s?(\\d[\\d,.]*)\\s?" + K));
  if (m && free(m.index)) {
    const hiK = !!m[4];
    const lower = Number(m[1].replace(/,/g, "")), upper = Number(m[3].replace(/,/g, ""));
    const loK = !!m[2] || (hiK && lower <= upper);
    out.min_price = money(m[1], loK); out.max_price = money(m[3], hiK); claim(m);
  }
  // single budgets
  if (out.max_price === null) {
    const pats = [
      new RegExp("[£$€]\\s?(\\d[\\d,]*(?:\\.\\d+)?)\\s?" + K + "?(?!\\s?(?:pp|per|a head|each))", "g"),
      new RegExp("(?<![\\d.])(\\d+(?:\\.\\d+)?)\\s?" + K + "\\b", "g"),
      new RegExp("(?:budget(?: of| is| around| about| up to)?|under|max(?:imum)?|up to|no more than|less than|nothing over|not over|tops|cap of|capped at)\\s+(\\d[\\d,]{2,})(?!\\s*(?:" + PEOPLE_WORDS + "))", "g"),
    ];
    for (const re of pats) {
      for (const mm of t.matchAll(re)) {
        if (!free(mm.index)) continue;
        out.max_price = money(mm[1], !!mm[2] && /k|grand|thousand/.test(mm[2]));
        claim(mm); break;
      }
      if (out.max_price !== null) break;
    }
  }
  if (/\bminimum spend|min spend|min\. spend/.test(t)) out.price_method = "minimum_spend";
  else if (/\b(hire fee|room hire fee|venue hire fee|hire cost|dry hire)\b/.test(t)) out.price_method = "hire_fee";
  if (/\b(cheap|affordable|not too (pricey|expensive|dear)|budget friendly|budget-friendly|low budget|inexpensive|reasonabl[ey]( priced)?)\b/.test(t)) out.budget_sensitive = true;

  // claim non-guest numbers: dates, times, durations, ages, years
  const skip = [
    /\b\d{1,2}\s?(?:am|pm)\b/g, /\b\d{1,2}[:.]\d{2}\b/g, /\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/g,
    new RegExp("\\b\\d{1,2}(?:st|nd|rd|th)?\\s+(?:of\\s+)?" + MON_ANY + "\\b", "g"),
    new RegExp("\\b" + MON_ANY + "\\s+(?:[12]?\\d|3[01])(?:st|nd|rd|th)?\\b(?!\\s?(?:" + PEOPLE_WORDS + "))", "g"),
    /\b\d+\s?(?:days?|nights?|weeks?|hours?|hrs?|mins?|minutes?|months?|years? old|yo)\b/g,
    /\b\d+(?:st|nd|rd|th)\b/g, /\b(?:19|20)\d{2}\b/g, /\bturning \d+\b/g, /\baged? \d+\b/g, /\bunder \d+s\b/g,
  ];
  for (const re of skip) for (const mm of t.matchAll(re)) claim(mm);

  // guests – ranges first (lower number)
  const range = new RegExp("(\\d+)\\s?(?:-|to|or|and)\\s?(\\d+)\\s?(?:" + PEOPLE_WORDS + ")", "g");
  const range2 = /\b(?:between|for)\s+(\d+)\s?(?:-|to|and)\s?(\d+)\b/g;
  for (const re of [range, range2]) for (const mm of t.matchAll(re)) if (free(mm.index) && out.guests === null) { out.guests = +mm[1]; claim(mm); }
  if (out.guests === null) {
    const pats = [
      new RegExp("(\\d+)\\s?(?:-?ish\\s)?(?:-?\\s?person|" + PEOPLE_WORDS + ")\\b", "g"),
      /\b(?:team|group|party|company|crew|gang|bunch|squad) of (\d+)\b/g,
      /\b(?:for|about|around|approx(?:imately)?|roughly|circa|c\.|maybe|max|up to|upto|over|at least|min(?:imum)?)\s+(\d+)\b(?!\s?(?:k|grand|pp|%|th|st|nd|rd)\b)/g,
      /\b(\d+)\s?(?:ish|odd|\+)(?=\W)/g,
      /\b(\d+)\s+(?:standing|seated|sat down|sit[- ]down|dudes|kiddos|kiddies|gals|chaps|ladies|lads|attendees|heads|pax)\b/g,
    ];
    for (const re of pats) {
      for (const mm of t.matchAll(re)) {
        const n = +mm[1];
        if (!free(mm.index + mm[0].indexOf(mm[1])) || n < 1 || n > 10000) continue;
        out.guests = n; claim(mm); break;
      }
      if (out.guests !== null) break;
    }
  }
  // last resort: one plain number left over ("corporate dinner 80", "club dinner dance 150")
  if (out.guests === null) {
    for (const mm of t.matchAll(new RegExp(String.raw`(?<![\d£$€.,:\/-])\b(\d{1,4})\b(?!\d|[.,]\d|[:\/%]|\s?(?:k|grand|thousand|am|pm|st|nd|rd|th|s\b|yo|years?|hours?|hrs?|mins?|minutes?|days?|weeks?|months?|nights?|pp|per|each|a head|percent|x\b))`, "g"))) {
      const n = +mm[1];
      if (!free(mm.index) || n < 2 || n > 5000) continue;
      out.guests = n; claim(mm); break;
    }
  }
}

// ---------------------------------------------------------------- 4. dates & times
function extractDates(t, out, todayIso, country) {
  const today = new Date(todayIso + "T12:00:00Z");
  const fmt = (d) => `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
  const future = (d) => { if (d < today) d.setUTCFullYear(d.getUTCFullYear() + 1); return d; };
  let m;
  if ((m = t.match(new RegExp("\\b(next|this|coming)\\s+(" + WEEKDAYS.join("|") + ")\\b")))) {
    const target = WEEKDAYS.indexOf(m[2]); const cur = (today.getUTCDay() + 6) % 7;
    let delta = (target - cur + 7) % 7 || 7;
    const d = new Date(today); d.setUTCDate(d.getUTCDate() + delta); out.date = fmt(d);
  } else if ((m = t.match(new RegExp("\\ba week (?:on|from) (" + WEEKDAYS.join("|") + ")\\b")))) {
    const target = WEEKDAYS.indexOf(m[1]); const cur = (today.getUTCDay() + 6) % 7;
    const d = new Date(today); d.setUTCDate(d.getUTCDate() + ((target - cur + 7) % 7 || 7) + 7); out.date = fmt(d);
  } else if (/\btomorrow\b/.test(t)) {
    const d = new Date(today); d.setUTCDate(d.getUTCDate() + 1); out.date = fmt(d);
  } else if (/\btoday\b|\btonight\b/.test(t)) {
    out.date = fmt(today);
  } else if ((m = t.match(new RegExp("\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(" + MON_ANY.slice(3, -1) + ")\\b"))) ||
             (m = t.match(new RegExp("\\b(" + MON_ANY.slice(3, -1) + ")\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b(?!\\s?(?:" + PEOPLE_WORDS + "|k\\b|grand))")))) {
    const day = +(m[1].match(/\d/) ? m[1] : m[2]); const mon = monIndex(m[1].match(/\d/) ? m[2] : m[1]);
    const year = t.slice(m.index + m[0].length).match(/^\s*,?\s*(\d{4})\b/);
    const d = new Date(Date.UTC(year ? +year[1] : today.getUTCFullYear(), mon, day, 12)); out.date = fmt(year ? d : future(d));
  } else if ((m = t.match(/\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/))) {
    const y = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : today.getUTCFullYear();
    const us = country === "US" || country === "CA"; // month/day in North America
    const d = new Date(Date.UTC(y, +(us ? m[1] : m[2]) - 1, +(us ? m[2] : m[1]), 12)); out.date = fmt(m[3] ? d : future(d));
  }
  const vr = out.venue_requirements;
  if ((m = t.match(/\b(?:til|till|until|to|past)\s+(\d{1,2})\s?am\b/))) vr.late_finish = String(+m[1]).padStart(2, "0") + ":00";
  else if (/\b(midnight|late finish|late night|late licen[cs]e|finish late|stay late)\b/.test(t)) vr.late_finish = "00:00";

  // Flexible dates: "any friday in october", "a saturday in june", "weekend in may", "sometime in march", "next month"
  // The search takes one date, so these go in date_options (every matching day) and date stays empty.
  if (out.date === null) {
    const MON = "(" + MONTHS.join("|") + ")", DAY = "(" + WEEKDAYS.join("|") + "|weekend|weekday)s?";
    let mon = null, days = null;
    if ((m = t.match(new RegExp("\\b" + DAY + "\\s+(?:in|of|during|at the (?:start|end) of|in early|in late|in mid)\\s+(?:" + "the month of\\s+)?" + MON + "\\b")))) { days = m[1]; mon = m[2]; }
    else if ((m = t.match(new RegExp("\\b" + MON + "\\s+(?:on\\s+)?(?:a|any|one of the)\\s+" + DAY + "\\b")))) { mon = m[1]; days = m[2]; }
    else if ((m = t.match(new RegExp("\\b(?:sometime|some time|anytime|any time|any day|a day|date|flexible|early|mid|late|end of|start of|beginning of)\\s+(?:in\\s+)?(?:of\\s+)?" + MON + "\\b"))) ||
             (m = t.match(new RegExp("\\b(?:in|during|for)\\s+" + MON + "\\b(?!\\s+\\d)")))) { mon = m[1]; }
    else if (/\bnext month\b/.test(t)) { mon = MONTHS[(today.getUTCMonth() + 1) % 12]; }
    if ((m = t.match(new RegExp("\\b(?:any|a|on a|one)\\s+" + DAY + "\\b"))) && !days && mon) days = m[1];
    if (mon) {
      const mi = MONTHS.indexOf(mon);
      let year = today.getUTCFullYear();
      const last = new Date(Date.UTC(year, mi + 1, 0, 12));
      if (last < today) year++;
      const want = days === "weekend" ? [5, 6] : days === "weekday" ? [0, 1, 2, 3, 4] : days ? [WEEKDAYS.indexOf(days)] : null;
      const list = [];
      for (let d = new Date(Date.UTC(year, mi, 1, 12)); d.getUTCMonth() === mi; d.setUTCDate(d.getUTCDate() + 1)) {
        if (d < today) continue;
        if (want && !want.includes((d.getUTCDay() + 6) % 7)) continue;
        list.push(fmt(d));
      }
      out.date_flexible = { month: `${String(mi + 1).padStart(2, "0")}/${year}`, days: days || "any", dates: list };
      if (list.length) out.date = list[0]; // product decision: search the first matching date
    }
  }
}

// ---------------------------------------------------------------- 5. event type (priority rules)
function birthdaySlug(age, childContext) {
  if (age === 1) return "1st-birthday-party";
  if ([18, 21, 30, 40, 50].includes(age)) return `${age}${age === 21 ? "st" : "th"}-birthday-party`;
  if (age !== null && age < 13) return "kids-birthday-party";
  if (age !== null && age < 18) return "teen-party";
  if (childContext) return "kids-birthday-party";
  return "birthday-party";
}

function extractEvent(t, out, idx, spans, ctx) {
  const hits = [];
  let tt = t;
  for (const { p, slug, tier, re } of idx.evPhrases) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(tt))) {
      if (inSpans(m.index, spans)) continue;
      // context guards
      if (p === "do" && !/(?:\w+ )do\b/.test(tt.slice(Math.max(0, m.index - 15), m.index + 3))) continue;
      if (p === "launch" && /book launch/.test(tt)) continue;
      if (p === "shoot" && /\b(photo|film|video)/.test(tt) === false && !/\bshoot\b/.test(tt)) continue;
      if (/^leaving/.test(p) && /^\s?(from|at|out of)\b/.test(tt.slice(m.index + m[0].length))) continue;
      if (p === "retreat" && /yoga|wellness|meditation|pilates|spa\b/.test(tt)) continue;
      if (p === "drinks" && /open bar|drinks package|drinks provided/.test(tt)) continue;
      if (p === "ball" && !/\bball\b/.test(tt)) continue;
      if (p === "conference" && /^\s?(call|calls|calling|facilities|phone)/.test(tt.slice(m.index + m[0].length))) continue;
      if (p === "delegates" && /conference|seminar|summit/.test(tt) === false) { /* still a conference signal */ }
      hits.push({ slug, tier, len: p.length, pos: m.index, p });
      tt = tt.slice(0, m.index) + " ".repeat(m[0].length) + tt.slice(m.index + m[0].length); // consume
    }
  }
  // Birthday / age logic
  const ageM = t.match(/\b(\d{1,3})(?:st|nd|rd|th)\b(?!\s*(?:of\s+)?(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec))/) ||
               t.match(/\bturning (\d{1,3})\b/) || t.match(/\baged? (\d{1,2})\b/);
  const age = ageM ? +ageM[1] : null;
  const child = /\b(kids?|children|childs?|son|daughter|toddlers?|boy|girl|little ones?|nephew|niece|baby)\b/.test(t);
  const hasBirthdayWord = hits.some((h) => h.slug === "birthday-party" || h.slug === "birthday-dinner") || /\bturning \d+/.test(t);
  if (hasBirthdayWord || (age !== null && age <= 100 && !hits.some((h) => h.tier >= 4))) {
    const isDinner = hits.some((h) => h.slug === "birthday-dinner");
    let slug = birthdaySlug(age, child);
    if (isDinner && slug === "birthday-party") slug = "birthday-dinner";
    hits.push({ slug, tier: 4.5, len: 99, pos: 0 });
    if (age !== null && age < 25) out.venue_requirements.guest_min_age = age;
  }
  if (out.venue_requirements.guest_min_age === null && /\b(kids|children|family) (welcome|friendly)|family-friendly|child-friendly|kid-friendly|all ages|all-ages|any age|no age (limit|restriction)/.test(t)) out.venue_requirements.guest_min_age = 0;
  if (out.venue_requirements.guest_min_age === null) {
    if (child && !/baby shower|christening/.test(t)) out.venue_requirements.guest_min_age = /\b(teen|teenage)/.test(t) ? 13 : 0;
    else if (/\b(teens|teenagers|teenage)\b/.test(t)) out.venue_requirements.guest_min_age = 13;
  }
  // kids party without birthday stays kids-party; wedding: ceremony + reception -> wedding
  if (/ceremony/.test(t) && /reception/.test(t) && /wedding|married/.test(t)) hits.push({ slug: "wedding", tier: 4.6, len: 99, pos: 0 });
  // US: "holiday party" is Christmas in UK markets
  if (/holiday party|holiday season/.test(t)) hits.push({ slug: ctx.country === "US" || ctx.country === "CA" ? "holiday-party" : "christmas-party", tier: 5, len: 99, pos: 0 });

  if (hits.some((h) => h.p === "delegates") && hits.some((h) => h.p !== "delegates" && h.tier >= 4)) hits.splice(hits.findIndex((h) => h.p === "delegates"), 1);
  if (!hits.length) { out.event_type = ctx.lpEventSlug || null; return; }
  hits.sort((a, b) => b.tier - a.tier || b.len - a.len || a.pos - b.pos);
  out.event_type = hits[0].slug;
}

// ---------------------------------------------------------------- 6. space types
function extractSpaces(t, out, idx, spans) {
  const found = [];
  let tt = t;
  for (const { p, slug, re } of idx.spPhrases) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(tt))) {
      const before = tt.slice(Math.max(0, m.index - 12), m.index), after = tt.slice(m.index + m[0].length, m.index + m[0].length + 14);
      tt = tt.slice(0, m.index) + " ".repeat(m[0].length) + tt.slice(m.index + m[0].length);
      if (inSpans(m.index, spans)) continue;
      if (p === "outside" && /^\s?(of\s)?[a-z]/.test(after) && !/^\s?(space|area|seating|bit)/.test(after)) continue; // "outside London"
      if (p === "bar" && (/open\s$/.test(before) || /^\s?(mitzvah|mitzva|bat)/.test(after))) continue;
      if (p === "theatre" && /^\s?(style|layout|seating)/.test(after)) continue;
      if ((p === "pop up" || p === "pop-up") && !/shop|store|retail/.test(t)) continue;
      if (p === "club" && /\b(book|supper|golf|sports|football|night)\s?$/.test(before)) continue;
      if (p === "hall" && /\b(music|town)\s$/.test(before)) continue;
      if (p === "garden party" && found.includes("garden")) continue;
      if (!found.includes(slug)) found.push(slug);
    }
  }
  out.space_types = found.slice(0, 3);
}

// ---------------------------------------------------------------- 7. location
function extractLocation(t, out, vocab, idx, ctx, spans) {
  const key = `${ctx.lpCity}|${ctx.country}`;
  const loc = vocab.locations[key] || { areas: [] };
  const aliases = (vocab.area_aliases || {})[key] || {};
  idx.areaCache = idx.areaCache || new Map();
  let cands = idx.areaCache.get(key);
  if (!cands) { cands = []; idx.areaCache.set(key, cands); }
  if (!cands.length && (loc.areas.length || Object.keys(aliases).length)) {
  for (const a of loc.areas) {
    const n = a.toLowerCase().replace(/'/g, "").replace(/\btheater\b/g, "theatre");
    cands.push([n, a]);
    const par = n.match(/^(.+?)\s*\(([^)]+)\)$/);
    if (par) { cands.push([par[1].trim(), a]); if (par[2].length >= 4) cands.push([par[2].trim(), a]); }
    if (/-/.test(n)) cands.push([n.replace(/-/g, " "), a]);
    if (/ street$/.test(n)) cands.push([n.replace(/ street$/, " st"), a]); // "liverpool st"
  }
  for (const [k, v] of Object.entries(aliases)) cands.push([k, v]);
  const centre = loc.areas.find((a) => new RegExp("^(" + loc.city + " (cbd|city centre)|downtown " + loc.city + ")$", "i").test(a));
  if (centre) for (const k of ["cbd", "the cbd", "city centre", "city center", "downtown", "town centre", "centre of town"]) if (!cands.some((c) => c[0] === k)) cands.push([k, centre]);
  cands.sort((a, b) => b[0].length - a[0].length);
  for (const c of cands) c.push(wordRe(c[0]));
  }
  let best = null;
  for (const [needle, area, re] of cands) {
    if (needle === ctx.lpCity.toLowerCase() || !t.includes(needle)) continue;
    re.lastIndex = 0; let m;
    while ((m = re.exec(t))) {
      if (inSpans(m.index, spans)) continue;
      // "Hackney Wick", "Clapham Junction": a longer place name we don't have – don't map to the shorter area
      if (/^\s(wick|court|road|rd|street|st|lane|park|hill|green|common|village|square|marshes|downs|heath|rise|vale|gate|cross|wharf|bridge|junction|station|quay|fields)\b/.test(t.slice(m.index + m[0].length))) continue;
      if (!best || m.index < best.pos) best = { area, pos: m.index };
      break;
    }
  }
  if (best) out.location.area = best.area;
  // other city: "in Liverpool", "Leeds venue" etc. (only explicit "in/near/around <city>")
  const m = t.match(/\b(?:in|near|around|at|to|of|from|outside|close to|nearby)\s+([a-z][a-z .-]{2,30}?)(?=[,.!?]| - | for | with | on | next | this | and | or | under | preferably | ideally | instead| actually| not |\s*$)/g);
  // "Melbourne not Sydney", "not London actually, it's Bristol", "instead of Toronto": any other known city in the text
  const lp = ctx.lpCity.toLowerCase();
  if (!out.location.other_city && new RegExp("\\b(not|instead of|rather than|not in)\\s+" + esc(lp) + "\\b|\\b" + esc(lp) + "\\b[^.]{0,12}\\b(not|instead|actually)\\b|\\binstead\\b|\\bactually\\b").test(t)) {
    let best = null;
    for (const [low, disp] of idx.cityNames) {
      if (low === lp || low.length < 4 || loc.areas.some((a) => a.toLowerCase() === low)) continue;
      const g = wordRe(low); const mm = g.exec(t);
      if (mm && (!best || low.length > best[0].length)) best = [low, disp];
    }
    if (best) out.location.other_city = best[1];
  }
  if (m && !out.location.other_city) {
    for (const frag of m) {
      const name = frag.replace(/^\s*(in|near|around|at|to|of|from|outside|close to|nearby)\s+/, "").replace(/\s+(station|train station|city centre|city center|town centre|centre|center|area|city)$/, "").trim();
      if (idx.cityNames.has(name) && name !== ctx.lpCity.toLowerCase() &&
          !loc.areas.some((a) => a.toLowerCase() === name)) { out.location.other_city = idx.cityNames.get(name); break; }
    }
  }
}

// ---------------------------------------------------------------- 8. features, catering, layout, venue rules
function extractExtras(t, out, spans, raw) {
  // a phrase only counts when it isn't in a "not / no / without" span
  const has = (re) => { const g = new RegExp(re.source, "g"); let m; while ((m = g.exec(t))) { if (!inSpans(m.index, spans)) return true; if (!m[0].length) g.lastIndex++; } return false; };
  const any = (re) => re.test(t); // also counts negated mentions ("no stairs", "no noise limits")

  // Room features (sql.room_features)
  const feats = new Set();
  const F = [
    [3, /wedding licen[cs]e|licen[cs]ed for (the )?(weddings?|ceremon(y|ies)|legal|marriage)|legal (bit|ceremony|part)|legally (get )?married|civil ceremony|registrar/],
    [4, /\btens\b|temporary event notice|\bt\.?e\.?n\.?s?\b/],
    [5, /\bwi-?fi\b|\binternet\b|broadband/],
    [6, /natural light|daylight|bright|windows|sunlight|sunny/],
    [7, /air ?con|air-con|air conditioning|aircon|climate control/],
    [8, new RegExp(String.raw`projector|projection|(?<!green )\bscreens?\b|presentation|\bpresent\b|slides`)],
    [9, new RegExp(String.raw`\btvs?\b|telly|television|flat ?screen|big screen|(?<!green )\bscreens?\b`)],
    [10, /whiteboard|white board/],
    [11, /flip ?charts?/],
    [12, /\bpa\b|pa system|speakers?\b|sound system|\bmics?\b|microphones?|audio system/],
    [13, /video call|video ?conf|dial(l)?ing in|dial in|joining remotely|remote(ly)? (attendees|participants|joining)|half online|online attendees|conference call|hybrid|\bzoom\b|teams call|livestream|live stream|remote attendees/],
    [16, /storage|somewhere to store|cloakroom/],
    [17, /(our|my|their) own (music|playlist|spotify)|play (our|my) (own )?music|own playlist/],
    [18, /(our|my|their) own dj|bring (a |our |my )?(own )?dj|byo dj|dj (is |are )?(allowed|ok|okay|permitted|welcome)|(have|book) (our|a) (own )?dj/],
    [19, /quiet (space|area|corner)|breakout (room|space)|chill ?out (room|area)/],
    [21, /photo(graphy)? (equipment|gear|kit)|camera (equipment|gear|kit)|studio lights|strobes?/],
    [22, /video (equipment|gear|kit)|filming (equipment|gear|kit)|cameras? (provided|included)/],
    [23, /green ?screen/],
    [24, /backdrops?|cyc wall|infinity (wall|cove)|cyclorama/],
    [25, /dance ?floor|dancing/],
    [26, /soundproof|sound-?proof|acoustically treated/],
    [27, /mirrors?|mirrored wall/],
    [28, /lighting( equipment| rig)?\b|stage lights|\blights\b/],
  ];
  for (const [id, re] of F) if (has(re)) feats.add(id);
  if (/\ba tens?\b(?! (minute|min|people|guests|of us))|apply for a ten\b/.test(raw)) feats.add(4);
  // a wedding with the ceremony at the venue needs a licensed venue
  if (/\bwedding|getting married|get married\b/.test(t) && has(/ceremony|get married (there|at|on site)|married on site|vows/) && !/church (ceremony|wedding)|ceremony (at|in) (a |the )?(church|registry|register office)|ceremony elsewhere|registry office|register office/.test(t)) feats.add(3);
  out.room_features = [...feats].sort((a, b) => a - b);

  // Catering & drinks (sql.catering_options)
  const cat = new Set();
  const C = [
    [1, /(food|lunch|lunches|dinner|catering|meals?|breakfast|buffet|canap[eé]s)s? (is |are |to be |should be )?(included|provided|catered|on site|in-house)|(food|lunch|dinner|catering|meals?|breakfast)s? [^.,]{0,15}(sorted|done|handled|provided|included|taken care of) by the venue|venue (that )?(can )?(cater|caters|does food|serves food)|venue (to )?(sort|do|handle|provide)s? (the )?(food|lunch|catering)|food (included|provided)|lunch provided|catered|catering (included|provided)|in-?house catering|they do the food|with food\b|venue (does|provides) (the )?food|provide (the )?food|food and drink (included|provided)/],
    [2, /halal/], [3, /kosher/],
    [7, /\bbyob\b|\bbyo\b|bring (our|my|your) own (drinks?|alcohol|booze|wine|bottles?|champagne|prosecco)|own (alcohol|booze|drinks)/],
    [9, /free water|complimentary water/],
    [10, /tea (and|n|&) coffee|coffee (and|n|&) tea|hot drinks|tea\/coffee|coffee\/tea|free (coffee|tea)|(coffee|tea) (included|provided|on tap)|complimentary (coffee|tea)/],
    [14, /vegan/], [15, /gluten|coeliac|celiac/],
    [16, /own (food|caterers?|catering|chef)|external caterers?|external catering|outside caterers?|outside catering|bring (our|my|in) (own )?food|bring in (a |our own )?caterer|our caterer|choose (our|my) (own )?caterer/],
    [17, /approved (caterers?|list|suppliers)|preferred caterers?|recommended caterers?|(the )?venues? (own )?caterers|whatever caterers/],
    [18, /kitchen (we|i) can (use|cook)|kitchen to cook|cook (it |the food )?ourselves|kitchen (we can use|to use|access|facilities)|use (of )?(the |a )?kitchen|access to (a |the )?kitchen|prep(are)? (our own )?food/],
    [20, /vegetarian|veggie/],
    [21, /refreshments|snacks|nibbles|light bites/],
  ];
  for (const [id, re] of C) if (has(re)) cat.add(id);
  // plain "catering" / "venue does the catering" = venue catering, unless the Client brings their own
  if (!cat.has(16) && has(new RegExp(String.raw`(?<!(halal|kosher|vegan|vegetarian|veggie|gluten[- ]free|dietary) )\bcatering\b(?! (has|must|needs|should) to be (halal|kosher|vegan|vegetarian))|\bcatered\b|venue (to |will |can )?(does|do|provide|provides|supply|supplies|sort|sorts|handle|handles) (the )?(catering|food)`)) && !has(/self[- ]catering|catering (not needed|not required)|no catering|own catering|external catering|outside catering/)) cat.add(1);
  // drinks served by the venue (search option 8) and no buyout fee for own caterers (search option 1000)
  if (has(/(drinks|bar|alcohol|booze|wine|cocktails|prosecco|champagne|grog) (are |is |to be )?(provided|included|served|available|supplied)|venue (to |that can |can |will |does |do )?(serve|provide|sell|supply|supplies|does|do|handle|handles|sort|sorts)s? (the )?(catering and (the )?)?(drinks|alcohol|booze|bar|grog)|licensed bar|fully licensed|open bar|drinks package|bar tab|bar service|cash bar|serves? alcohol|drinks reception/)) cat.add(8);
  if (has(/no (buyout|buy-out|buy out) fee|without (a )?(buyout|buy-out|buy out)|no (extra )?(fee|charge) for (external|outside|our own|own) cater/)) cat.add(1000);
  if (cat.has(9) || cat.has(10)) { cat.delete(9); cat.delete(10); cat.add(21); } // search has one "Free refreshments" option
  out.catering_options = [...cat].sort((a, b) => a - b);

  // Layout (sql.room_layouts)
  const lay = [
    [/sit[- ]down|seated (dinner|meal|lunch|dining)|dining (layout|setup|set-up|style)|banquet style|long tables?|one long table|seated at tables/, 1],
    [/board ?meeting|boardroom (style|layout|table|setup|set-up|set up|seating)|around (one|1|a|the same) table|meeting table/, 2],
    [/cabaret|round tables/, 3], [/classroom|desks|school style/, 4],
    [/theat(re|er) (style|layout|seating|setup|set-up)|rows of (chairs|seats)|\bin rows\b|rows facing|audience|auditorium style|seated in rows/, 5],
    [/u[- ]?shape|in a u\b|horseshoe/, 6], [/standing|canap[eé]s|mingl|drinks reception|cocktail style|stand-?up/, 7],
  ];
  for (const [re, id] of lay) if (has(re)) { out.layout = id; break; }
  // "seminar 120 pax theatre", "conference for 200, theatre": a bare "theatre" in a business event is the layout
  if (out.layout === null && has(/\btheat(re|er)\b/) && /conference|seminar|agm|talk|presentation|lecture|summit|briefing|training|town hall|keynote|pax|delegates/.test(t) && !/\b(a|the|in a|at a|at the|in the) theat(re|er)\b/.test(t)) { out.layout = 5; out._theatreLayout = true; }
  if (out.layout === null && has(/\bboardroom\b/) && /meeting|board|exec|directors/.test(t)) out.layout = 2;
  if (out.layout === null && has(/\bseated\b|seating for|seats for|sit down|sit-down|chairs for (everyone|all)|everyone (sitting|seated)/)) out.layout = 100; // search option "Seating" (any seated layout)
  if (out.layout === null) {
    if (has(/\b(dinner|lunch|meal|supper)\s+for\s+(?:up to |about |around )?\d|\b(christening|team|work|business|christmas|xmas|family|wedding|sit-down) (dinner|lunch|meal)\b/)) out.layout = 1;
    else if (has(/\bdrinks\s+for\s+(?:up to |about |around )?\d|\b(leaving|after-work|after work|work|team|summer|christmas|xmas|networking) drinks\b/)) out.layout = 7;
  }

  // Area type (room_venue_part) – most specific first
  if (has(/exclusive (hire|use)|whole (venue|place|building|house|restaurant|bar|pub)|entire (venue|place|building)|buy ?out|take over the (whole |entire )?(venue|place)|hire the (whole|entire)/)) out.venue_part = "the_whole_venue";
  else if (has(/semi[- ]private (outdoor|garden|terrace)|(part|section|corner) of (the |a )?(terrace|garden|beer garden)/)) out.venue_part = "semi_private_outdoor_space";
  else if (has(/private (garden|terrace|outdoor|courtyard|roof ?terrace|rooftop)|(our|my) own (garden|terrace|outdoor)/)) out.venue_part = "private_outdoor_space";
  else if (has(/semi[- ]private|sectioned[- ]off|cordoned[- ]off|roped[- ]off|reserved (area|section)|area of (the|a) (bar|restaurant|pub)/)) out.venue_part = "semi_private_area_within_the_space";
  else if (has(/shared (space|area)|don'?t mind sharing|happy to share/)) out.venue_part = "shared_space";
  else if (has(/private (room|space|area|dining room)|own room|not shared|to ourselves|no other (guests|groups|people)|\bprivate\b(?! (party|parties|event|celebration|function|dinner|dining|hire|screening))|\bprivately\b/)) out.venue_part = "private_space_within_the_venue";

  // search accepts several area types: generic "private" means any private option
  out.venue_parts = out.venue_part === "private_space_within_the_venue" && !has(/private (room|space|area|dining room)|own room/)
    ? ["the_whole_venue", "private_space_within_the_venue", "private_outdoor_space"]
    : out.venue_part ? [out.venue_part] : [];

  // Accessibility (room + venue flags)
  const acc = [];
  if (any(/wheelchair|disabled access|mobility (issues|scooter)|uses a walker|accessible venue|fully accessible/)) acc.push("wheelchair_access");
  if (any(/(accessible|disabled|step[- ]free|wheelchair)[^.]{0,40}(toilet|loo|wc|bathroom|restroom|washroom)|(toilet|loo|wc|bathroom|restroom|washroom)s?[^.]{0,25}(accessible|step[- ]free|wheelchair)/)) acc.push("accessible_toilets");
  if (any(/step[- ]free|no stairs|no steps|ground floor|ground level|level access|on one level|without stairs/)) acc.push("step_free");
  if (any(new RegExp(String.raw`(?<!(goods|cargo|service|freight) )\blifts?\b|elevator`))) acc.push("lift");
  if (any(/cargo lift|goods lift|service lift|freight (lift|elevator)|load[- ]in/)) acc.push("cargo_lift");
  if (any(/disabled parking|blue badge|accessible parking|handicap(ped)? (parking|spots?|spaces?)|ada parking/)) acc.push("disabled_parking");
  out.accessibility = acc;

  // Venue-level rules (sql.venues, allowed events, cancellation policies)
  const vr = out.venue_requirements;
  if (has(/ticket(ed|s)?\b|selling tickets|sell tickets|paid entry|entry fee|on the door|promoted event|public event/)) vr.ticketed_event = true;
  if (has(/\bdj\b|live band|\bband\b|loud music|loud|blast(ing)? (the )?music|music (up|blasting|pumping)|dancing|rave|club night|party all night|live music|music (until|till) late/)) vr.loud_music = true;
  if (has(/free cancell?ation|cancell?ation[- ]free|cancell?ation (is |for )?free|free to cancel|cancel (for )?free|no cancell?ation (fee|charge)|refundable|flexi(ble)? (cancell?ation|cancel|booking)|can cancel|full refund|cancel[^.]{0,30}(deposit|money back|refund|without (losing|penalty)|w\/o losing)|lose (our|the) deposit/)) vr.flexible_cancellation = true;
  if (has(/reschedul|move the date|change the date|flexible (on )?dates?|postpone/)) vr.rescheduling_allowed = true;
  if (has(/accommodation|overnight|stay over|stay the night|rooms for (guests|the night)|bedrooms|sleep(s|ing)?\b|place to stay|hotel rooms/)) vr.accommodation = true;
  if (has(/free parking/)) vr.parking = "free";
  else if (has(/on[- ]?site parking|parking on site|car park on site/)) vr.parking = "onsite";
  else if (has(/parking|car park|park the car|space for cars|somewhere to park/)) vr.parking = "any";
  if (has(/extension|extend (the )?(time|licen[cs]e)|stay later|go on later|run over|(til|till|until) late/)) vr.late_extension = true;
  if (any(/corkage/)) vr.no_corkage = true; // Clients only mention corkage to avoid it
  if (has(/top[- ]rated|best[- ]rated|highly rated|super ?venue|best venues?|well reviewed|great reviews|reliable venue/)) vr.supervenue = true;
  // adults only / age limits
  if (has(/over 21s|over-21s|21\+|21 and over|no under 21s/)) vr.guest_min_age = Math.max(vr.guest_min_age || 0, 21);
  else if (has(/adults? only|over 18s|over-18s|18\+|18 and over|no kids|no children|child-?free/)) { vr.guest_min_age = vr.guest_min_age === null ? 18 : vr.guest_min_age; vr.adults_only = true; }
  // under-18s / under-25s coming → hide venues with an age policy (search: no_age_restriction)
  vr.no_age_restriction = vr.guest_min_age !== null && vr.guest_min_age < 25 && !vr.adults_only && !/over 21s|over-21s|21\+|no under 21s/.test(t);
  // cuisine (sql.venues.main_cuisine)
  const CU = { british: "british", english: "british", american: "american", italian: "italian", indian: "indian", european: "european",
    australian: "australian", mediterranean: "mediterranean", asian: "asian", japanese: "japanese", sushi: "japanese", french: "french",
    mexican: "mexican", "latin american": "latin_american", "south american": "latin_american", brazilian: "latin_american", peruvian: "latin_american",
    "middle eastern": "middle_eastern", lebanese: "middle_eastern", turkish: "middle_eastern", persian: "middle_eastern",
    spanish: "spanish", tapas: "spanish", chinese: "chinese", "dim sum": "chinese", african: "african", nigerian: "african", ethiopian: "african",
    thai: "thai", irish: "irish", greek: "greek", scottish: "scottish", canadian: "canadian", argentinian: "argentinian", argentine: "argentinian",
    singaporean: "singaporean", malaysian: "singaporean", pizza: "italian", pasta: "italian", curry: "indian",
    tacos: "mexican", burritos: "mexican", nachos: "mexican", paella: "spanish", ramen: "japanese", mezze: "middle_eastern", kebab: "middle_eastern" };
  const cuRe = new RegExp("\\b(" + Object.keys(CU).sort((a, b) => b.length - a.length).join("|") + ")\\b(?:\\s+(food|cuisine|menu|restaurant|dinner|meal|lunch|feast|buffet|banquet|style|place|night))?");
  const cu = t.match(cuRe);
  if (cu && (cu[2] || ["sushi", "tapas", "dim sum", "pizza", "curry", "pasta", "tacos", "burritos", "nachos", "paella", "ramen", "mezze", "kebab"].includes(cu[1]) || /(proper|authentic|real)\s+$/.test(t.slice(0, cu.index)) || /restaurant|dinner|lunch|meal|food|eat/.test(t))) vr.cuisine = CU[cu[1]];
}

// ---------------------------------------------------------------- 9. venue type (Search Page "Venue Type" filter = venue_types[] room tag IDs)
const VENUE_TYPES = [
  [296, /cocktail (bar|lounge)/], [196, /\b(wine )?bars?\b|taproom|brewery/], [187, /\bhotels?\b/], [197, /\brestaurants?\b|\bbistro\b|\bbrasserie\b/],
  [182, /\bpubs?\b|boozer|tavern|\binn\b|gastropub/], [194, /\bcafe|coffee shop/], [208, /nightclub|night club|club night/], [238, /\bboat|yacht|barge|riverboat/],
  [79, /cinema|screening room/], [226, /galler(y|ies)/], [188, /museum/], [181, /\btheatre\b(?! (style|layout|seating))/], [191, /community (centre|center|hall)|village hall/],
  [269, /conference (centre|center|venue)/], [274, /co-?working/], [270, /meeting (centre|center)/], [272, /universit(y|ies)|college|academic|lecture (theatre|hall)/],
  [179, /church|chapel/], [180, /warehouse/], [268, /apartment|\bflat\b/], [178, /\bhall\b(?! (style|layout))/], [254, /\bloft\b/], [32, /dance studio/],
  [277, /photo studio|film studio|photography studio|photo \/ film studio/], [273, /sports club|clubhouse|golf club|cricket club|rugby club|tennis club/],
];
function extractVenueTypes(t, out, spans) {
  const ids = [];
  for (const [id, re] of VENUE_TYPES) {
    const g = new RegExp(re.source, "g"); let m;
    while ((m = g.exec(t))) { if (!inSpans(m.index, spans)) { if (!ids.includes(id)) ids.push(id); break; } }
  }
  if (ids.includes(296) && ids.includes(196) && !/\b(wine )?bars?\b/.test(t.replace(/cocktail (bar|lounge)s?/g, ""))) ids.splice(ids.indexOf(196), 1); // "cocktail bar" alone isn't also "bar"
  if (/open bar|bar tab|cash bar|bar service|bar mitzvah|bat mitzvah/.test(t) && !/\b(a|the|in a|at a) (wine |cocktail )?bar\b/.test(t)) { const i = ids.indexOf(196); if (i > -1) ids.splice(i, 1); }
  out.venue_types = ids;
}

// ---------------------------------------------------------------- 10. search URL params (matches the live Search Page, Oct 2026)
const STYLE_IDS = { "modern-venues": 234, "luxury-venues": 201, "unusual-space": 143, "unique": 143, "industrial": 308, "vintage": 309,
  "colorful": 307, "funky-venues": 307, "view": 306, "waterfront": 305, "riverside-venues": 305 };
const SEARCH_FEATURES = new Set([3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 21, 22, 23, 24, 25, 26, 27, 28]);
const SEARCH_CATERING = new Set([1, 2, 3, 7, 8, 14, 15, 16, 18, 20, 21, 1000, 1001]);
const ACCESS_KEYS = { wheelchair_access: "wheelchairAccessible", accessible_toilets: "disabledToilets", step_free: "groundLevel",
  disabled_parking: "disabledParking", lift: "liftAllFloors", cargo_lift: "cargoLift" };
// venue-type tags are sent as venue_types[], not room_tags[]
const VENUE_TYPE_SLUGS = new Set(["bar-venues", "hotel-venues", "restaurant", "pub", "cafe-venues", "nightclub-hire", "boat", "cinema", "gallery",
  "museum-venues", "theatre", "community-centre", "coworking-space", "church", "warehouse", "apartment", "hall", "loft", "dance-studio"]);
export function buildSearch(f, vocab) {
  const ids = (vocab && vocab.tag_ids) || {};
  const p = [];
  const add = (k, v) => p.push([k, String(v)]);
  if (f.guests) add("people", f.guests);
  if (f.date) add("date", f.date);
  if (f.min_price) add("min_price", f.min_price);
  if (f.max_price) add("max_price", f.max_price);
  else if (f.price_per_person_max) { add("max_price", f.price_per_person_max); }
  const method = f.price_method || (!f.max_price && f.price_per_person_max ? "per_person" : null);
  if (method) add("price_method", method);
  if (f.layout) add("room_layout", f.layout);
  const tags = new Set();
  for (const s of f.space_types) { if (STYLE_IDS[s]) tags.add(STYLE_IDS[s]); else if (!VENUE_TYPE_SLUGS.has(s) && ids[s]) tags.add(ids[s]); }
  for (const id of tags) add("room_tags[]", id);
  for (const id of f.venue_types) add("venue_types[]", id);
  for (const v of f.venue_parts) add("venue_parts[]", v);
  for (const id of f.room_features) if (SEARCH_FEATURES.has(id)) add("room_features[]", id);
  const cat = new Set(f.catering_options.filter((x) => SEARCH_CATERING.has(x)));
  if (f.venue_requirements.no_corkage) cat.add(1001);
  for (const id of [...cat].sort((a, b) => a - b)) add("catering_options[]", id);
  if (f.venue_requirements.ticketed_event) add("allowed_events[]", 13);
  for (const a of f.accessibility) if (ACCESS_KEYS[a]) add("accessibility_features[]", ACCESS_KEYS[a]);
  if (f.venue_requirements.supervenue) add("supervenues_only", "true");
  if (f.venue_requirements.flexible_cancellation || f.venue_requirements.rescheduling_allowed) add("flexible_cancellation_only", "true");
  if (f.venue_requirements.no_age_restriction) add("no_age_restriction", "true");
  const slug = f.event_type || f.space_types.find((s) => !STYLE_IDS[s]) || "event-venue";
  // not filterable on the Search Page – pass to the enquiry instead
  const vr = f.venue_requirements, enquiry = [];
  if (f.room_features.includes(4)) enquiry.push("TENs");
  if (vr.late_finish) enquiry.push("late finish " + vr.late_finish);
  if (vr.late_extension) enquiry.push("late extension");
  if (vr.accommodation) enquiry.push("accommodation");
  if (vr.parking) enquiry.push(vr.parking === "free" ? "free parking" : "parking");
  if (vr.cuisine) enquiry.push(vr.cuisine.replace(/_/g, " ") + " food");
  if (vr.loud_music) enquiry.push("loud music");
  if (f.catering_options.includes(17)) enquiry.push("approved caterers");
  if (vr.adults_only) enquiry.push("adults only");
  return { room_tag: slug, params: p, not_filterable: enquiry };
}

// ---------------------------------------------------------------- main
export function parseSearch(text, ctx, vocab) {
  if (!INDEX || INDEX.vocab !== vocab) { INDEX = buildIndex(vocab); INDEX.vocab = vocab; }
  const out = {
    intent: "venue_search", location: { area: null, other_city: null }, event_type: null, space_types: [],
    guests: null, date: null, date_flexible: null, price_method: null, venue_types: [], venue_parts: [], min_price: null, max_price: null, price_per_person_max: null, budget_sensitive: false,
    room_features: [], catering_options: [], layout: null, venue_part: null, accessibility: [],
    venue_requirements: { adults_only: false, no_age_restriction: false, guest_min_age: null, ticketed_event: false, loud_music: false, late_finish: null,
      flexible_cancellation: false, rescheduling_allowed: false, accommodation: false, parking: null, cuisine: null,
      late_extension: false, no_corkage: false, supervenue: false },
  };
  const raw = String(text || "").slice(0, 500);
  // only real support questions are "not_a_search" (show the Help Centre link); gibberish or too-short text is "unclear" (just run a generic search)
  if (NOT_SEARCH.test(raw.toLowerCase())) { out.intent = "not_a_search"; return out; }
  if (!/[a-z]{2,}/i.test(raw)) { out.intent = "unclear"; out.search = buildSearch(out, vocab); return out; }
  const t = normalise(raw, INDEX);
  const spans = negatedSpans(t);
  extractNumbers(t, out);
  extractDates(t, out, ctx.today, ctx.country);
  extractEvent(t, out, INDEX, spans, ctx);
  extractSpaces(t, out, INDEX, spans);
  extractLocation(t, out, vocab, INDEX, ctx, spans);
  // combined tags: a wedding + garden / outdoor is its own Room Tag
  if (out.event_type === "wedding" && out.space_types.includes("garden")) out.event_type = "garden-wedding";
  else if (out.event_type === "wedding" && out.space_types.includes("outdoor")) out.event_type = "outdoor-wedding";
  extractExtras(t, out, spans, raw.toLowerCase());
  extractVenueTypes(t, out, spans);
  if (out._theatreLayout) { out.venue_types = out.venue_types.filter((i) => i !== 181); out.space_types = out.space_types.filter((s) => s !== "theatre"); }
  delete out._theatreLayout;
  if (new RegExp(String.raw`(?<![A-Za-z])(AC|A\/C)(?![A-Za-z])`).test(String(text || "")) && !out.room_features.includes(7)) { out.room_features.push(7); out.room_features.sort((a, b) => a - b); }
  const signal = out.event_type || out.space_types.length || out.guests || out.location.area || out.location.other_city ||
    out.max_price || out.price_per_person_max || out.room_features.length || out.catering_options.length;
  if (!signal && raw.trim().split(/\s+/).length <= 3) out.intent = "unclear";
  out.search = buildSearch(out, vocab);
  return out;
}
